import { useState, type FormEvent } from "react"
import { useAuth } from "../context/AuthContext"
import { sessionExpired } from "../services/api"
import "../styles/auth.scss"

/**
 * Pantallas de sesión. Portadas del tablero de reuniones, que a su vez comparte el
 * chrome .auth-page / .auth-card con calendar-app: login, "sin permiso" y "elegir
 * contraseña" se ven igual en las tres apps internas.
 */

export function LoadingScreen({ text = "Cargando…" }: { text?: string }) {
  return (
    <div className="auth-page">
      <div className="auth-loading">{text}</div>
    </div>
  )
}

/** El servidor no está configurado (falta SUPABASE_ANON_KEY) o no contestó. */
export function ConfigError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>No se pudo iniciar el tablero</h1>
        <p className="auth-subtitle">{message}</p>
        <div className="auth-actions">
          <button type="button" className="btn btn-primary" onClick={onRetry}>
            Reintentar
          </button>
        </div>
      </div>
    </div>
  )
}

export function SignIn() {
  const { signIn, requestPasswordReset } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      await signIn(email.trim(), password)
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo iniciar sesión.")
    } finally {
      setBusy(false)
    }
  }

  async function onReset() {
    if (!email.trim()) {
      setError("Escribí tu email primero.")
      return
    }
    setError(null)
    setBusy(true)
    try {
      await requestPasswordReset(email.trim())
      setNotice("Te enviamos un mail para restablecer la contraseña.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo enviar el mail.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={onSubmit}>
        <h1>Tablero de horas extra</h1>
        <p className="auth-subtitle">Panel de administración. Ingresá con tu cuenta.</p>

        {sessionExpired && !error && (
          <div className="auth-notice">Tu sesión expiró. Volvé a entrar.</div>
        )}
        {notice && <div className="auth-notice">{notice}</div>}
        {error && <div className="auth-error">{error}</div>}

        <div className="auth-input-container">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="auth-input-container">
          <label htmlFor="password">Contraseña</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="auth-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Entrando…" : "Entrar"}
          </button>
          <button type="button" className="auth-link" onClick={onReset} disabled={busy}>
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        {/* Sin auto-registro: este texto es lo que evita el ticket de soporte. */}
        <p className="auth-hint">
          Las cuentas las crea IT. Si no tenés acceso, escribile a IT. Es la misma cuenta
          que usás en el tablero de reuniones y en la app de calendario.
        </p>
      </form>
    </div>
  )
}

/** Completa un invite o un reset. Llega acá por el hash type=invite / type=recovery. */
export function SetPassword() {
  const { setPassword, signOut } = useAuth()
  const [pw, setPw] = useState("")
  const [pw2, setPw2] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (pw.length < 8) {
      setError("La contraseña tiene que tener al menos 8 caracteres.")
      return
    }
    if (pw !== pw2) {
      setError("Las contraseñas no coinciden.")
      return
    }
    setBusy(true)
    try {
      await setPassword(pw)
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar la contraseña.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={onSubmit}>
        {/* La pantalla SE AUTO-IDENTIFICA a propósito: el template del mail de recovery
            es de todo el proyecto Supabase y lo comparten calendar-app y el tablero de
            reuniones, así que quien hizo clic necesita saber dónde aterrizó. */}
        <h1>Panel de horas extra</h1>
        <p className="auth-subtitle">Elegí una contraseña nueva para tu cuenta.</p>

        {error && <div className="auth-error">{error}</div>}

        <div className="auth-input-container">
          <label htmlFor="new-pw">Contraseña nueva</label>
          <input
            id="new-pw"
            type="password"
            autoComplete="new-password"
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
          />
        </div>

        <div className="auth-input-container">
          <label htmlFor="new-pw2">Repetila</label>
          <input
            id="new-pw2"
            type="password"
            autoComplete="new-password"
            required
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
          />
        </div>

        <div className="auth-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Guardando…" : "Guardar y entrar"}
          </button>
          <button type="button" className="auth-link" onClick={() => void signOut()}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  )
}

/**
 * Usuario autenticado pero SIN rol de admin.
 *
 * Esta pantalla es alcanzable en la práctica, no en teoría: el pool auth.users es el
 * mismo que el de calendar-app y el del tablero de reuniones, así que CUALQUIER usuario
 * de esas apps puede autenticarse acá. Si fuera vaga o no tuviera cómo salir, sería un ticket de soporte.
 *
 * El botón "Reintentar" importa de verdad: app_metadata viaja DENTRO del JWT, así que
 * un permiso otorgado después del login no se ve hasta que el token refresque. Sin ese
 * botón, la persona recién habilitada tendría que adivinar que debe salir y volver a entrar.
 */
export function NoAccess({
  email,
  onRetry,
  retrying,
}: {
  email: string | null
  onRetry: () => void
  retrying: boolean
}) {
  const { signOut } = useAuth()
  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>No tenés permiso</h1>
        <p className="auth-subtitle">
          Tu cuenta {email ? <span className="auth-email">({email})</span> : null} no tiene
          permiso para ver este tablero. El acceso es solo para HR y administradores:
          pedíselo a IT.
        </p>
        <div className="auth-actions">
          <button type="button" className="btn btn-primary" onClick={onRetry} disabled={retrying}>
            {retrying ? "Verificando…" : "Reintentar"}
          </button>
          <button type="button" className="btn" onClick={() => void signOut()}>
            Cerrar sesión
          </button>
        </div>
        <p className="auth-hint">
          Si acabás de recibir el permiso, "Reintentar" renueva tu sesión para que tome
          efecto.
        </p>
      </div>
    </div>
  )
}
